/**
 * useClienteProdutos - Hook for managing cliente products
 *
 * Server-side pagination, sorting, and debounced search.
 * Handles: Products list, CRUD, import, GTINs management
 */
import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import type { MRT_SortingState, MRT_PaginationState } from 'mantine-react-table';
import { Cliente } from '../services/clienteService';
import produtoClienteService, { produtoClienteGTINService } from '../services/produtoClienteService';
import type { ProductoCliente, ProductoClienteGTIN } from '../types/produtoCliente';
import { msg, MESSAGES } from '../utils/messages';

export interface UseClienteProdutosConfig {
  selectedCliente: Cliente | null;
  activeTab: string;
}

export interface UseClienteProdutosReturn {
  produtos: ProductoCliente[];
  loadingProdutos: boolean;
  produtosCount: number;
  activeCount: number;
  totalRowCount: number;
  showDeleted: boolean;
  setShowDeleted: (value: boolean) => void;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  globalSearch: string;
  setGlobalSearch: (search: string) => void;
  sorting: MRT_SortingState;
  setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
  loadProdutos: () => Promise<void>;
  handleSaveProduto: (produtoData: any, editingProduto: ProductoCliente | null) => Promise<void>;
  handleDeleteProduto: (produto: ProductoCliente, confirmFn: (opts: any) => Promise<boolean>) => Promise<void>;
  handleRestoreProduto: (produto: ProductoCliente, confirmFn: (opts: any) => Promise<boolean>) => Promise<void>;
  // N74: devolvem o resultado da importacao — o ImportModal monta o resumo com ele.
  handleImportProdutos: (file: File) => Promise<any>;
  handleImportGTINs: (file: File) => Promise<any>;
  loadGTINs: (produto: ProductoCliente) => Promise<ProductoClienteGTIN[]>;
  handleAddGTIN: (produto: ProductoCliente, gtinData: any) => Promise<ProductoClienteGTIN[]>;
  handleUpdateGTIN: (produto: ProductoCliente, gtinId: number, gtinData: any) => Promise<ProductoClienteGTIN[]>;
  handleDeleteGTIN: (produto: ProductoCliente, gtinId: number, confirmFn: (opts: any) => Promise<boolean>) => Promise<ProductoClienteGTIN[]>;
}

export const useClienteProdutos = ({
  selectedCliente,
  activeTab,
}: UseClienteProdutosConfig): UseClienteProdutosReturn => {
  // Server-side pagination state
  const [produtos, setProdutos] = useState<ProductoCliente[]>([]);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [globalSearch, setGlobalSearch] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'codigo', desc: false }]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);

  // Counts for header (lightweight)
  const [produtosCount, setProdutosCount] = useState(0);

  // Debounced search (300ms)
  const [debouncedSearch] = useDebouncedValue(globalSearch, 300);

  // Fetch counts on client selection (always, lightweight < 5ms)
  useEffect(() => {
    if (!selectedCliente) {
      setProdutosCount(0);
      return;
    }
    produtoClienteService.fetchCounts(selectedCliente.id)
      .then(c => setProdutosCount(c.total))
      .catch(() => {});
  }, [selectedCliente]);

  // Fetch page data (only when Produtos tab is active)
  const fetchPage = useCallback(async () => {
    if (!selectedCliente || activeTab !== 'produtos') return;
    setLoadingProdutos(true);
    try {
      const sortField = sorting[0]?.id || 'codigo';
      const sortDir = sorting[0]?.desc ? '-' : '';
      const data = await produtoClienteService.listarPaginado(selectedCliente.id, {
        page: pagination.pageIndex + 1, // MRT is 0-indexed, backend is 1-indexed
        pageSize: pagination.pageSize,
        search: debouncedSearch || undefined,
        ordering: `${sortDir}${sortField}`,
        showDeleted,
      });
      setProdutos(data.items);
      setTotalRowCount(data.total);
      setActiveCount(data.active_count);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    } finally {
      setLoadingProdutos(false);
    }
  }, [selectedCliente, activeTab, pagination, debouncedSearch, sorting, showDeleted]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // Reset pagination when search/client/filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [debouncedSearch, selectedCliente, showDeleted]);

  // After import/CRUD: refresh counts + current page
  const refreshAfterMutation = useCallback(async () => {
    await fetchPage();
    if (selectedCliente) {
      const c = await produtoClienteService.fetchCounts(selectedCliente.id);
      setProdutosCount(c.total);
    }
  }, [fetchPage, selectedCliente]);

  // Save produto (create or update)
  const handleSaveProduto = useCallback(async (
    produtoData: any,
    editingProduto: ProductoCliente | null
  ) => {
    if (!selectedCliente) return;

    try {
      if (editingProduto) {
        await produtoClienteService.atualizar(
          selectedCliente.id,
          editingProduto.id,
          produtoData
        );
        msg.success(MESSAGES.CLIENT.PRODUCT_UPDATED);
      } else {
        await produtoClienteService.criar(selectedCliente.id, produtoData);
        msg.success(MESSAGES.CLIENT.PRODUCT_CREATED);
      }
      await refreshAfterMutation();
    } catch (err: any) {
      console.error('Erro ao salvar produto:', err);
      // Surfacar a mensagem util do backend (ex.: codigo pertence a produto
      // EXCLUIDO -> orienta a usar "Mostrar excluidos" e Restaurar). Mesmo
      // padrao do handleRestoreProduto. Antes mostrava sempre o generico.
      msg.error(err.response?.data?.detail || MESSAGES.CLIENT.PRODUCT_SAVE_ERROR);
      throw err;
    }
  }, [selectedCliente, refreshAfterMutation]);

  // Delete produto
  const handleDeleteProduto = useCallback(async (
    produto: ProductoCliente,
    confirmFn: (opts: any) => Promise<boolean>
  ) => {
    if (!selectedCliente) return;

    const confirmed = await confirmFn({
      title: 'Excluir Produto',
      message: MESSAGES.CLIENT.PRODUCT_CONFIRM_DELETE(produto.codigo),
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return;

    try {
      await produtoClienteService.excluir(selectedCliente.id, produto.id);
      await refreshAfterMutation();
      msg.success(MESSAGES.CLIENT.PRODUCT_DELETED);
    } catch (err: any) {
      console.error('Erro ao excluir produto:', err);
      msg.error(MESSAGES.CLIENT.PRODUCT_DELETE_ERROR);
    }
  }, [selectedCliente, refreshAfterMutation]);

  // Restore produto (undo soft delete)
  const handleRestoreProduto = useCallback(async (
    produto: ProductoCliente,
    confirmFn: (opts: any) => Promise<boolean>
  ) => {
    if (!selectedCliente) return;

    const confirmed = await confirmFn({
      title: 'Restaurar Produto',
      message: MESSAGES.CLIENT.PRODUCT_CONFIRM_RESTORE(produto.codigo),
      confirmText: 'Restaurar',
    });

    if (!confirmed) return;

    try {
      await produtoClienteService.restaurar(selectedCliente.id, produto.id);
      await refreshAfterMutation();
      msg.success(MESSAGES.CLIENT.PRODUCT_RESTORED);
    } catch (err: any) {
      console.error('Erro ao restaurar produto:', err);
      msg.error(err.response?.data?.detail || MESSAGES.CLIENT.PRODUCT_RESTORE_ERROR);
    }
  }, [selectedCliente, refreshAfterMutation]);

  // Import produtos
  // N74 (Alana): passa a DEVOLVER o resultado e a propagar o erro, em vez de
  // exibir a mensagem aqui. Quem mostra o resultado agora e o ImportModal
  // (reusado nesta tela para trazer o modo "colar do Excel"), que precisa do
  // result para montar o resumo. Se os dois exibissem, sairia toast duplicado e
  // o modal leria `undefined` como "nenhum produto importado".
  const handleImportProdutos = useCallback(async (file: File) => {
    if (!selectedCliente) {
      throw new Error('Selecione um cliente antes de importar.');
    }

    try {
      const result = await produtoClienteService.importarProdutos(selectedCliente.id, file);
      await refreshAfterMutation();
      return result;
    } catch (err: any) {
      console.error('Erro ao importar produtos:', err);
      throw err;
    }
  }, [selectedCliente, refreshAfterMutation]);

  // Import GTINs
  const handleImportGTINs = useCallback(async (file: File) => {
    if (!selectedCliente) {
      throw new Error('Selecione um cliente antes de importar.');
    }

    try {
      const result = await produtoClienteService.importarGTINs(selectedCliente.id, file);
      await refreshAfterMutation();
      return result;
    } catch (err: any) {
      console.error('Erro ao importar GTINs:', err);
      throw err;
    }
  }, [selectedCliente, refreshAfterMutation]);

  // Load GTINs for a produto
  const loadGTINs = useCallback(async (produto: ProductoCliente): Promise<ProductoClienteGTIN[]> => {
    if (!selectedCliente) return [];

    try {
      const gtins = await produtoClienteGTINService.listar(selectedCliente.id, produto.codigo);
      return gtins;
    } catch (err: any) {
      console.error('Erro ao carregar GTINs:', err);
      msg.error(MESSAGES.CLIENT.GTIN_LOAD_ERROR);
      return [];
    }
  }, [selectedCliente]);

  // Add GTIN
  const handleAddGTIN = useCallback(async (
    produto: ProductoCliente,
    gtinData: any
  ): Promise<ProductoClienteGTIN[]> => {
    if (!selectedCliente) return [];

    try {
      await produtoClienteGTINService.adicionar(
        selectedCliente.id,
        produto.codigo,
        gtinData
      );
      const gtins = await produtoClienteGTINService.listar(selectedCliente.id, produto.codigo);
      msg.success(MESSAGES.CLIENT.GTIN_ADDED);
      return gtins;
    } catch (err: any) {
      console.error('Erro ao adicionar GTIN:', err);
      msg.error(MESSAGES.CLIENT.GTIN_ADD_ERROR);
      return [];
    }
  }, [selectedCliente]);

  // Update GTIN
  const handleUpdateGTIN = useCallback(async (
    produto: ProductoCliente,
    gtinId: number,
    gtinData: any
  ): Promise<ProductoClienteGTIN[]> => {
    if (!selectedCliente) return [];

    try {
      await produtoClienteGTINService.atualizar(
        selectedCliente.id,
        produto.codigo,
        gtinId,
        gtinData
      );
      const gtins = await produtoClienteGTINService.listar(selectedCliente.id, produto.codigo);
      msg.success('GTIN atualizado com sucesso');
      return gtins;
    } catch (err: any) {
      console.error('Erro ao atualizar GTIN:', err);
      msg.error('Erro ao atualizar GTIN');
      return [];
    }
  }, [selectedCliente]);

  // Delete GTIN
  const handleDeleteGTIN = useCallback(async (
    produto: ProductoCliente,
    gtinId: number,
    confirmFn: (opts: any) => Promise<boolean>
  ): Promise<ProductoClienteGTIN[]> => {
    if (!selectedCliente) return [];

    const confirmed = await confirmFn({
      title: 'Excluir GTIN',
      message: MESSAGES.CLIENT.GTIN_CONFIRM_DELETE,
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return [];

    try {
      await produtoClienteGTINService.excluir(selectedCliente.id, produto.codigo, gtinId);
      const gtins = await produtoClienteGTINService.listar(selectedCliente.id, produto.codigo);
      msg.success(MESSAGES.CLIENT.GTIN_DELETED);
      return gtins;
    } catch (err: any) {
      console.error('Erro ao excluir GTIN:', err);
      msg.error(MESSAGES.CLIENT.GTIN_DELETE_ERROR);
      return [];
    }
  }, [selectedCliente]);

  return {
    produtos,
    loadingProdutos,
    produtosCount,
    activeCount,
    totalRowCount,
    showDeleted,
    setShowDeleted,
    pagination,
    setPagination,
    globalSearch,
    setGlobalSearch,
    sorting,
    setSorting,
    loadProdutos: refreshAfterMutation,
    handleSaveProduto,
    handleDeleteProduto,
    handleRestoreProduto,
    handleImportProdutos,
    handleImportGTINs,
    loadGTINs,
    handleAddGTIN,
    handleUpdateGTIN,
    handleDeleteGTIN,
  };
};
